<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <Transition name="modal-slide">
          <div v-if="show" class="declaration-modal">
            <!-- Header -->
            <div class="modal-header">
              <div class="header-icon-wrapper">
                <div class="icon-pulse"></div>
                <div class="header-icon">
                  <i class="fas fa-money-bill-wave"></i>
                </div>
              </div>
              <div class="header-content">
                <h2>💸 Déclaration de Perte Financière</h2>
                <p>Signalez une perte d'argent (perdu, erreur, manquant)</p>
              </div>
              <button @click="closeModal" class="close-btn">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Corps du modal -->
            <div class="modal-body">
              <!-- Type de perte -->
              <div class="loss-type-selector single-type">
                <div 
                  class="type-card" 
                  :class="{ active: formData.typePerte === 'vol' }"
                  @click="formData.typePerte = 'vol'"
                >
                  <div class="type-icon vol">
                    <i class="fas fa-user-secret"></i>
                  </div>
                  <div class="type-info">
                    <h4>Perdu</h4>
                    <p>Argent volé ou disparu</p>
                  </div>
                  <div class="type-check" v-if="formData.typePerte === 'vol'">
                    <i class="fas fa-check-circle"></i>
                  </div>
                </div>
              </div>

              <!-- Formulaire -->
              <div class="declaration-form">
                <div class="form-row">
                  <div class="form-group">
                    <label>
                      <i class="fas fa-money-bill-alt"></i>
                      Montant perdu (TND) *
                    </label>
                    <input 
                      type="number" 
                      v-model.number="formData.montant" 
                      class="form-input montant-input"
                      placeholder="0.000"
                      step="0.001"
                      min="0"
                      required
                    >
                  </div>
                  
                  <div class="form-group">
                    <label>
                      <i class="fas fa-calendar-alt"></i>
                      Date de l'incident *
                    </label>
                    <input 
                      type="date" 
                      v-model="formData.dateIncident" 
                      class="form-input"
                      :max="today"
                      required
                    >
                  </div>
                </div>

                <div class="form-group" v-if="formData.typePerte === 'vol'">
                  <label>
                    <i class="fas fa-file-alt"></i>
                    Numéro de dépôt de plainte
                  </label>
                  <input 
                    type="text" 
                    v-model="formData.numeroPlainte" 
                    class="form-input"
                    placeholder="Numéro de la plainte (optionnel)"
                  >
                </div>

                <div class="form-group">
                  <label>
                    <i class="fas fa-comment-alt"></i>
                    Description détaillée *
                  </label>
                  <textarea 
                    v-model="formData.description" 
                    class="form-input textarea"
                    placeholder="Décrivez les circonstances de la perte..."
                    rows="4"
                    required
                  ></textarea>
                </div>

                <div class="form-group">
                  <label>
                    <i class="fas fa-camera"></i>
                    Justificatifs (photos, documents)
                  </label>
                  <div class="photo-upload-zone" @click="triggerFileUpload">
                    <input 
                      ref="fileInput"
                      type="file" 
                      accept="image/*,.pdf"
                      multiple
                      @change="handleFileUpload"
                      style="display: none"
                    >
                    <div class="upload-content">
                      <i class="fas fa-cloud-upload-alt"></i>
                      <p>Cliquez pour ajouter des justificatifs</p>
                      <small>Images ou PDF - Max 5MB par fichier</small>
                    </div>
                  </div>
                  <div v-if="formData.justificatifs.length > 0" class="files-preview">
                    <div v-for="(file, index) in formData.justificatifs" :key="index" class="file-item">
                      <i class="fas fa-file"></i>
                      <span>Fichier {{ index + 1 }}</span>
                      <button @click="removeFile(index)" class="remove-file">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Résumé -->
              <div class="summary-section" v-if="formData.montant > 0">
                <div class="summary-card">
                  <div class="summary-icon">
                    <i class="fas fa-exclamation-triangle"></i>
                  </div>
                  <div class="summary-content">
                    <h4>Résumé de la déclaration</h4>
                    <p>Montant déclaré perdu : <strong>{{ formatMontant(formData.montant) }} TND</strong></p>
                    <p class="warning-text">
                      <i class="fas fa-info-circle"></i>
                      Cette déclaration sera soumise à validation par l'administrateur.
                      Si validée, le montant sera déduit de la caisse globale.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="modal-footer">
              <button @click="closeModal" class="btn btn-secondary">
                <i class="fas fa-times"></i>
                Annuler
              </button>
              <button 
                @click="submitDeclaration" 
                class="btn btn-primary"
                :disabled="!isFormValid || isSubmitting"
              >
                <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-paper-plane"></i>
                {{ isSubmitting ? 'Envoi en cours...' : 'Soumettre la déclaration' }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'DeclarationPerteModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    userRole: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'declaration-submitted'],
  setup(props, { emit }) {
    const toast = useToast()
    const fileInput = ref(null)
    const isSubmitting = ref(false)
    
    const today = computed(() => new Date().toISOString().split('T')[0])
    
    const formData = ref({
      typePerte: 'vol',
      montant: 0,
      dateIncident: today.value,
      numeroPlainte: '',
      description: '',
      justificatifs: []
    })

    const isFormValid = computed(() => {
      return formData.value.montant > 0 && 
             formData.value.dateIncident && 
             formData.value.description.trim().length > 0 &&
             formData.value.typePerte
    })

    const formatMontant = (montant) => {
      return new Intl.NumberFormat('fr-TN', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      }).format(montant)
    }

    const closeModal = () => {
      emit('close')
      resetForm()
    }

    const resetForm = () => {
      formData.value = {
        typePerte: 'vol',
        montant: 0,
        dateIncident: today.value,
        numeroPlainte: '',
        description: '',
        justificatifs: []
      }
    }

    const triggerFileUpload = () => {
      fileInput.value?.click()
    }

    const handleFileUpload = (event) => {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        if (file.size > 5 * 1024 * 1024) {
          toast.error('Le fichier est trop volumineux (max 5MB)')
          return
        }
        const reader = new FileReader()
        reader.onload = (e) => {
          formData.value.justificatifs.push(e.target.result)
        }
        reader.readAsDataURL(file)
      })
    }

    const removeFile = (index) => {
      formData.value.justificatifs.splice(index, 1)
    }

    const submitDeclaration = async () => {
      if (!isFormValid.value) {
        toast.error('Veuillez remplir tous les champs obligatoires')
        return
      }

      isSubmitting.value = true

      try {
        const token = localStorage.getItem('token')
        const payload = {
          typePerte: formData.value.typePerte,
          montant: formData.value.montant,
          dateIncident: formData.value.dateIncident,
          description: formData.value.description,
          justificatifs: formData.value.justificatifs,
          ...(formData.value.typePerte === 'vol' && { numeroPlainte: formData.value.numeroPlainte })
        }

        const response = await fetch('/api/caissier/declaration-perte', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        })

        if (response.ok) {
          const result = await response.json()
          toast.success('✅ Déclaration de perte soumise avec succès !')
          emit('declaration-submitted', result)
          closeModal()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la soumission')
        }
      } catch (error) {
        console.error('Erreur:', error)
        toast.error('Erreur lors de la soumission de la déclaration')
      } finally {
        isSubmitting.value = false
      }
    }

    watch(() => props.show, (newVal) => {
      if (newVal) {
        formData.value.dateIncident = today.value
      }
    })

    return {
      fileInput,
      isSubmitting,
      formData,
      today,
      isFormValid,
      formatMontant,
      closeModal,
      triggerFileUpload,
      handleFileUpload,
      removeFile,
      submitDeclaration
    }
  }
}
</script>

<style scoped>
/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-slide-leave-active {
  transition: all 0.3s ease-in;
}
.modal-slide-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}
.modal-slide-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

/* Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

/* Modal Container */
.declaration-modal {
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 650px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

/* Header */
.modal-header {
  padding: 24px 28px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.header-icon-wrapper {
  position: relative;
  width: 60px;
  height: 60px;
}

.icon-pulse {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 0; }
  100% { transform: scale(1); opacity: 0.5; }
}

.header-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.header-content {
  flex: 1;
}

.header-content h2 {
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.header-content p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  margin: 0;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

/* Body */
.modal-body {
  padding: 24px 28px;
  overflow-y: auto;
  flex: 1;
}

/* Type Selector */
.loss-type-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.loss-type-selector.single-type {
  grid-template-columns: 1fr;
  max-width: 300px;
}

.type-card {
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  position: relative;
}

.type-card:hover {
  border-color: #d1d5db;
  transform: translateY(-2px);
}

.type-card.active {
  border-color: #ef4444;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
}

.type-icon {
  width: 45px;
  height: 45px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.type-icon.vol {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.type-icon.erreur {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.type-icon.autre {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
}

.type-info h4 {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
}

.type-info p {
  margin: 0;
  font-size: 0.7rem;
  color: #6b7280;
}

.type-check {
  position: absolute;
  top: 8px;
  right: 8px;
  color: #10b981;
  font-size: 16px;
}

/* Form */
.declaration-form {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.9rem;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.montant-input {
  font-size: 1.2rem;
  font-weight: 600;
  color: #ef4444;
}

.form-input.textarea {
  resize: vertical;
  min-height: 80px;
}

/* Photo Upload */
.photo-upload-zone {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #f9fafb;
}

.photo-upload-zone:hover {
  border-color: #ef4444;
  background: #fef2f2;
}

.upload-content i {
  font-size: 32px;
  color: #9ca3af;
  margin-bottom: 8px;
}

.upload-content p {
  margin: 0 0 4px 0;
  color: #4b5563;
  font-weight: 500;
  font-size: 0.9rem;
}

.upload-content small {
  color: #9ca3af;
  font-size: 0.75rem;
}

.files-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 0.8rem;
}

.remove-file {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: #ef4444;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

/* Summary */
.summary-section {
  margin-top: 16px;
}

.summary-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 16px;
  border: 1px solid #f59e0b;
}

.summary-icon {
  width: 40px;
  height: 40px;
  background: #f59e0b;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
}

.summary-content h4 {
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #92400e;
}

.summary-content p {
  margin: 0 0 4px 0;
  font-size: 0.85rem;
  color: #78350f;
}

.warning-text {
  font-size: 0.75rem !important;
  color: #92400e !important;
  margin-top: 8px !important;
}

/* Footer */
.modal-footer {
  padding: 16px 28px;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  border: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-primary {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 640px) {
  .loss-type-selector {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
